import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Card, { CARD_SHADOW } from '../../core/Card';
import Chip from '../../core/Chip';
import Table, { TABLE_ALIGN } from '../../core/Table';
import SelectDropdown, { SELECT_DROPDOWN_SIZE } from '../../core/SelectDropdown';
import InlineChipInput from '../../core/InlineChipInput';
import IconButton, { ICON_BUTTON_VARIANT } from '../../core/IconButton';
import { useTranslations } from '../../context/TranslationContext';
import {
    EVALUATION_STEP_CARD_COLUMN,
    EVALUATION_STEP_CARD_COLUMN_WIDTH,
    EVALUATION_STEP_CARD_ICON,
    EVALUATION_STEP_CARD_LEGACY_CLASS,
} from './constants';

const LEGACY = EVALUATION_STEP_CARD_LEGACY_CLASS;
import { normalizeRows, toSelectOptions } from './helpers';

/**
 * One evaluation step: a card whose header picks the recruitment step (with duplicate / remove
 * actions), a Skill · Interview question · Input format table, and a footer select that adds
 * more skills. Fully controlled — every change is reported through a callback.
 */
const EvaluationStepCard = ({
    stepId,
    stepOptions = [],
    onStepChange = () => {},
    stepDisabled = false,
    rows = [],
    ratingOptions = [],
    onQuestionChange = () => {},
    onScoreTypeChange = () => {},
    onRowRemove = () => {},
    skillOptions = [],
    onSkillAdd = () => {},
    onDuplicate,
    onRemove,
    removeDisabled = false,
    portalMenus = true,
    labels = {},
    className,
    id,
    ...rest
}) => {
    const { t } = useTranslations();
    const items = normalizeRows(rows);
    const text = {
        stepPlaceholder: labels.stepPlaceholder || t('No step selected'),
        skill: labels.skill || t('Skill'),
        question: labels.question || t('Interview question'),
        format: labels.format || t('Input format'),
        actions: labels.actions || t('Actions'),
        addSkills: labels.addSkills || t('Add skills'),
        duplicate: labels.duplicate || t('Duplicate'),
        remove: labels.remove || t('Remove'),
        removeRow: labels.removeRow || t('Remove'),
        empty: labels.empty || t('No skills added to this step yet.'),
    };

    const columns = [
        { id: EVALUATION_STEP_CARD_COLUMN.SKILL, header: text.skill, width: EVALUATION_STEP_CARD_COLUMN_WIDTH.SKILL },
        { id: EVALUATION_STEP_CARD_COLUMN.QUESTION, header: text.question },
        { id: EVALUATION_STEP_CARD_COLUMN.FORMAT, header: text.format, width: EVALUATION_STEP_CARD_COLUMN_WIDTH.FORMAT },
        { id: EVALUATION_STEP_CARD_COLUMN.ACTIONS, header: text.actions, width: EVALUATION_STEP_CARD_COLUMN_WIDTH.ACTIONS, align: TABLE_ALIGN.RIGHT, srOnly: true },
    ];

    return (
        <Card shadow={CARD_SHADOW.XS} className={classNames('ds-evaluation-step-card', LEGACY.CARD, className)} id={id} {...rest}>
            <div className={classNames('ds-evaluation-step-card__header', LEGACY.HEADER)}>
                <SelectDropdown
                    className={classNames('ds-evaluation-step-card__step', LEGACY.STEP_SELECT)}
                    id={id ? `${id}-step` : undefined}
                    options={toSelectOptions(stepOptions)}
                    value={stepId ?? null}
                    placeholder={text.stepPlaceholder}
                    onChange={(value) => value != null && onStepChange(value)}
                    disabled={stepDisabled}
                    searchable={false}
                    size={SELECT_DROPDOWN_SIZE.LG}
                    portal={portalMenus}
                />
                <div className={classNames('ds-evaluation-step-card__actions', LEGACY.ACTIONS)}>
                    {typeof onDuplicate === 'function' && (
                        <IconButton
                            variant={ICON_BUTTON_VARIANT.PLAIN}
                            className="ds-evaluation-step-card__icon-button"
                            icon={EVALUATION_STEP_CARD_ICON.DUPLICATE}
                            label={text.duplicate}
                            title={text.duplicate}
                            onClick={onDuplicate}
                        />
                    )}
                    {typeof onRemove === 'function' && (
                        <IconButton
                            variant={ICON_BUTTON_VARIANT.PLAIN}
                            className="ds-evaluation-step-card__icon-button"
                            icon={EVALUATION_STEP_CARD_ICON.REMOVE}
                            label={text.remove}
                            title={text.remove}
                            disabled={removeDisabled}
                            onClick={onRemove}
                        />
                    )}
                </div>
            </div>

            <Table columns={columns} emptyText={text.empty} className={classNames('ds-evaluation-step-card__table', LEGACY.TABLE)}>
                {items.map((row) => (
                    <Table.Row key={row.id} disabled={row.disabled}>
                        <Table.Cell>
                            <Chip value={row.title} className="ds-evaluation-step-card__skill" />
                        </Table.Cell>
                        <Table.Cell>
                            <InlineChipInput
                                value={row.question}
                                disabled={row.disabled}
                                onChange={(value) => onQuestionChange(row.id, value)}
                            />
                        </Table.Cell>
                        <Table.Cell>
                            <SelectDropdown
                                className={classNames('ds-evaluation-step-card__format', LEGACY.RATING_SELECT)}
                                options={toSelectOptions(ratingOptions)}
                                value={row.scoreType || null}
                                onChange={(value) => value != null && onScoreTypeChange(row.id, value)}
                                disabled={row.disabled}
                                searchable={false}
                                size={SELECT_DROPDOWN_SIZE.SM}
                                portal={portalMenus}
                            />
                        </Table.Cell>
                        <Table.Cell align={TABLE_ALIGN.RIGHT}>
                            <IconButton
                                variant={ICON_BUTTON_VARIANT.PLAIN}
                                className="ds-evaluation-step-card__icon-button"
                                icon={EVALUATION_STEP_CARD_ICON.REMOVE_ROW}
                                label={`${text.removeRow} ${row.title}`}
                                title={text.removeRow}
                                disabled={row.disabled}
                                onClick={() => onRowRemove(row.id)}
                            />
                        </Table.Cell>
                    </Table.Row>
                ))}
            </Table>

            <div className={classNames('ds-evaluation-step-card__footer', LEGACY.FOOTER)}>
                <SelectDropdown
                    className={classNames('ds-evaluation-step-card__add-skill', LEGACY.SKILL_SELECT)}
                    id={id ? `${id}-skills` : undefined}
                    options={toSelectOptions(skillOptions)}
                    value={null}
                    placeholder={text.addSkills}
                    onChange={(value) => value != null && onSkillAdd(value)}
                    clearOnChange
                    size={SELECT_DROPDOWN_SIZE.LG}
                    disabled={!skillOptions.length}
                    portal={portalMenus}
                />
            </div>
        </Card>
    );
};

EvaluationStepCard.propTypes = {
    /** Selected recruitment step id */
    stepId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    /** Steps available for this card: `{ id, text }` */
    stepOptions: PropTypes.array,
    /** `(stepId)` */
    onStepChange: PropTypes.func,
    /** Lock the step select (an applicant was already evaluated on it) */
    stepDisabled: PropTypes.bool,
    /** Skill rows: `{ id, title, question, scoreType, disabled }` */
    rows: PropTypes.array,
    /** Input formats: `{ id, text }` */
    ratingOptions: PropTypes.array,
    /** `(rowId, question)` */
    onQuestionChange: PropTypes.func,
    /** `(rowId, scoreType)` */
    onScoreTypeChange: PropTypes.func,
    /** `(rowId)` */
    onRowRemove: PropTypes.func,
    /** Skills not yet in this step: `{ id, text }`; empty disables the footer select */
    skillOptions: PropTypes.array,
    /** `(skillId)` */
    onSkillAdd: PropTypes.func,
    /** Header duplicate action; omit to hide */
    onDuplicate: PropTypes.func,
    /** Header remove action; omit to hide */
    onRemove: PropTypes.func,
    /** Disable the header remove action */
    removeDisabled: PropTypes.bool,
    /** Render the option menus in a body portal so the card and the table's scroll container
     * cannot clip them; turn off only where the page already portals the card itself */
    portalMenus: PropTypes.bool,
    /** Translated texts: `{ stepPlaceholder, skill, question, format, actions, addSkills, duplicate, remove, removeRow, empty }` */
    labels: PropTypes.object,
    /** Extra class */
    className: PropTypes.string,
    /** Id prefix for the selects */
    id: PropTypes.string,
};

export {
    EVALUATION_STEP_CARD_ICON,
    EVALUATION_STEP_CARD_COLUMN,
    EVALUATION_STEP_CARD_COLUMN_WIDTH,
    EVALUATION_STEP_CARD_LEGACY_CLASS,
    normalizeRows,
    toSelectOptions,
};
export default EvaluationStepCard;
